import { focusHaloFlush, MorphPanel, panelMotion, usePanelDismiss } from "@pax/ui";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

/** A quiet word beside a heading that opens, in place, into the choices it stands for. */
export function Picker<T>({
  options,
  value,
  onChange,
  label,
  align = "left",
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  /** The edge the word and its choices hold to. */
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = usePanelDismiss<HTMLSpanElement>({ open, onDismiss: () => setOpen(false) });
  const selected = options.find(option => option.value === value);
  const face = "flex h-6 items-center gap-0.5 whitespace-nowrap px-1.5 text-xs";
  return (
    <span ref={rootRef} className="relative inline-block">
      <span aria-hidden className={`invisible ${face}`}>
        {selected?.label}
        <ChevronDown className="size-3" />
      </span>
      <span
        className={`absolute top-0 ${align === "left" ? "left-0" : "right-0"} ${open ? "z-30" : "z-10"}`}
      >
        <MorphPanel
          view={open ? "open" : "closed"}
          anchor={align === "left" ? "top-left" : "top-right"}
          className={
            open
              ? "rounded-xl bg-popover shadow-[0_4px_12px_rgb(0_0_0/0.06),0_16px_32px_-12px_rgb(0_0_0/0.2)] ring-1 ring-foreground/10"
              : `rounded-lg hover:bg-foreground/5 ${panelMotion}`
          }
        >
          {open ? (
            <div className="w-28 p-0.5">
              {options.map(option => (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={option.value === value}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex h-6 w-full items-center justify-between gap-2 rounded-lg px-1.5 text-left text-xs hover:bg-foreground/[0.07] ${panelMotion} ${focusHaloFlush} ${option.value === value ? "font-medium" : "text-muted-foreground"}`}
                >
                  {option.label}
                  {option.value === value && <Check className="size-3 shrink-0" />}
                </button>
              ))}
            </div>
          ) : (
            <button
              type="button"
              aria-label={label}
              aria-expanded={false}
              onClick={() => setOpen(true)}
              className={`${face} text-muted-foreground/70 hover:text-foreground ${focusHaloFlush}`}
            >
              {selected?.label}
              <ChevronDown className="size-3" />
            </button>
          )}
        </MorphPanel>
      </span>
    </span>
  );
}
